/**
 * Stimulation Clicker upgrades.
 *
 * Each upgrade does two things: adds passive stimulation per second, and turns
 * on a visual layer. The layers are the joke — the page gets progressively more
 * unusable, and the numbers are just the excuse. Costs grow steeply so the chaos
 * arrives in a readable order rather than all at once.
 */
export type Effect =
  | 'rain'
  | 'emoji'
  | 'podcast'
  | 'notifications'
  | 'ticker'
  | 'subway'
  | 'asmr'
  | 'confetti'
  | 'shake';

export type Upgrade = {
  id: Effect;
  label: string;
  blurb: string;
  emoji: string;
  cost: number;
  /** Stimulation per second added per copy owned. */
  rate: number;
  /** Copies you can buy. Most are one-shot toggles; a few stack. */
  max: number;
};

export const UPGRADES: Upgrade[] = [
  {
    id: 'rain',
    label: 'Rain sounds',
    blurb: 'Focus-enhancing. Allegedly.',
    emoji: '🌧️',
    cost: 25,
    rate: 1,
    max: 1
  },
  {
    id: 'emoji',
    label: 'Floating emoji',
    blurb: 'They drift. Nobody knows why.',
    emoji: '✨',
    cost: 80,
    rate: 3,
    max: 3
  },
  {
    id: 'notifications',
    label: 'Notifications',
    blurb: 'Somebody liked something.',
    emoji: '🔔',
    cost: 200,
    rate: 8,
    max: 3
  },
  {
    id: 'podcast',
    label: 'A podcast',
    blurb: 'Two men are explaining discipline.',
    emoji: '🎙️',
    cost: 500,
    rate: 20,
    max: 1
  },
  {
    id: 'ticker',
    label: 'Breaking news ticker',
    blurb: 'All of it is breaking.',
    emoji: '📺',
    cost: 1_200,
    rate: 45,
    max: 1
  },
  {
    id: 'subway',
    label: 'Gameplay footage',
    blurb: 'Bottom third of the screen, obviously.',
    emoji: '🚇',
    cost: 3_000,
    rate: 110,
    max: 1
  },
  {
    id: 'asmr',
    label: 'ASMR whispering',
    blurb: 'Extremely close to your ear.',
    emoji: '👂',
    cost: 7_500,
    rate: 260,
    max: 1
  },
  {
    id: 'confetti',
    label: 'Unearned confetti',
    blurb: 'You have achieved nothing. Celebrate.',
    emoji: '🎉',
    cost: 18_000,
    rate: 600,
    max: 1
  },
  {
    id: 'shake',
    label: 'Screen shake',
    blurb: 'For engagement.',
    emoji: '📳',
    cost: 40_000,
    rate: 1_400,
    max: 1
  }
];

/** Cost of the nth copy — each one you already own makes the next dearer. */
export const costFor = (upgrade: Upgrade, owned: number) =>
  Math.round(upgrade.cost * Math.pow(2.4, owned));

/** Headlines for the news ticker. All equally urgent. */
export const TICKER = [
  'BREAKING: Study finds studies',
  'Man reads entire terms of service, hospitalised',
  'Scientists confirm the thing you already believed',
  'Local trolley problem resolves itself',
  'Nation agrees to stop arguing, briefly',
  'Emoji shortage enters third week',
  'Sources say sources'
];

/** Podcast lines. Nobody is listening, including them. */
export const PODCAST = [
  'and that’s really what discipline is about',
  'I get up at four. Some people get up at five.',
  'so my cold plunge guy says',
  'we’re gonna take a quick word from our sponsor',
  'but is it really a habit if you enjoy it',
  'let me push back on that slightly'
];

export const NOTIFICATIONS = [
  'Someone liked your post',
  'Your streak is at risk',
  '3 people are typing',
  'New follower',
  'Your weekly report is ready',
  'Reminder: hydrate',
  'Someone tagged a factiii'
];
